/* eslint-disable prefer-arrow-callback */
/**
 * Video player analytics plugin and metrics tracking
 * @see https://gaiatv.atlassian.net/browse/GT-641
 */
import _assign from 'lodash/assign'
import _cloneDeep from 'lodash/cloneDeep'
import _findIndex from 'lodash/findIndex'
import _forEach from 'lodash/forEach'
import _get from 'lodash/get'
import _isEqual from 'lodash/isEqual'
import _isFunction from 'lodash/isFunction'
import _isNil from 'lodash/isNil'
import _isNumber from 'lodash/isNumber'
import _isString from 'lodash/isString'
import _isUndefined from 'lodash/isUndefined'
import _last from 'lodash/last'
import _map from 'lodash/map'
import _size from 'lodash/size'
import { log as logError } from '../../log/error'
import {
  add as videoAnalyticsStAdd,
  update as videoAnalyticsStUpdate,
  VA_DEFAULT_PLAYER_TYPE,
} from '../../services/video-analytics/index'

export const PLUGIN_NAME = 'SmfAnalytics'

const MAX_EVENT_SIZE = 1500 // more than this is rediculous-would be thrown out (payload too large)

/* qualified view event fires when the user reaches 15 seconds of video playback */
const QUALIFIED_VIEW_DURATION = 15000

/**
 * An SMF analytics events
 * @typedef SMFAnalyticsEvent
 * @type {Object}
 * @property {Number} [timestamp] The timestamp when the event occured
 * @property {String} name The name for the event
 * @property {String} [kind] For texttracks the kind
 * @property {String} [lang] For texttracks the language
 */

/**
 * Create a video analytics event
 * @param {String} name The event name
 * @param {Object} [additionProps] Additional event properties
 * @param {String} [additionProps.kind] For texttracks the kind
 * @param {String} [additionProps.lang] For texttracks the language
 * @param {Number} [timestamp] The event timestamp in milliseconds
 * @returns {SMFAnalyticsEvent} The event
 */
function createEvent (name, player, additionProps, timestamp) {
  return _assign({
    timestamp: timestamp || getCurrentTimeStamp(),
    position: getPlayerPosition(player),
    name,
  }, additionProps ? _cloneDeep(additionProps) : {})
}

/**
 * Get the current Javascript timestamp in milliseconds
 * @returns The current timestamp in milliseconds
 */
function getCurrentTimeStamp () {
  return Date.now()
}

/**
 * Get the current player position
 * @param {*} player A videoJs player instance
 * @returns The current player time
 */
function getPlayerPosition (player) {
  if (player && player.currentTime()) {
    return Math.round(player.currentTime())
  }
  return 0
}

/** The default st data type */
const SMF_ANALYTICS_ST_DATA_TYPE_DEFAULT = 'default'

/** The extended st data type with histogram, etc properties */
const SMF_ANALYTICS_ST_DATA_TYPE_EXTENDED = 'extended'

/**
 * Human-readable descriptions for media error codes.
 * Standard HTML5/Video.js: 1-4; some players (e.g. Brightcove) use non-standard codes like -2.
 * Used so New Relic and other tooling show a descriptive error instead of a bare code.
 */
const MEDIA_ERROR_CODE_DESCRIPTIONS = {
  1: 'MEDIA_ERR_ABORTED (user aborted playback)',
  2: 'MEDIA_ERR_NETWORK (network error)',
  3: 'MEDIA_ERR_DECODE (decoding error)',
  4: 'MEDIA_ERR_SRC_NOT_SUPPORTED (source not supported)',
  5: 'MEDIA_ERR_ENCRYPTED (encrypted media error)',
  0: 'MEDIA_ERR_CUSTOM (custom player error)',
  [-2]: 'Decoding/source error (non-standard code -2); often decoder or codec failure',
}

/**
 * Handle video player tracking of events, positions, and histogram
 */
class SMFAnalytics {
  /**
   * The user id
   * @type {Number}
   */
  uid = null

  /**
   * The anonymous uuid if uid is not available
   * @type {String}
   */
  anonymousUuid = null

  /**
   * The event captured by the video player
   * @type {SMFAnalyticsEvent[]}
   */
  events = []

  /** The media id we are tracking */
  mediaId = null

  /** The video content id for the media we are tracking */
  videoId = null

  /** The st update id */
  stId = null

  /** The video analytics uuid for the session */
  stVAUuid = null

  /** The video analytics session token */
  stToken = null

  /** The beacon polling interval */
  beaconInterval = 6000

  /** The histogram resolution in milliseconds */
  histogramResolution = 30000

  /** The last position seen during beacon */
  lastBeaconPosition = 0

  /** The last beacon timestamp */
  lastBeaconTimestamp = 0

  /** How often should the plugin beacon send data to the api in milliseconds */
  beaconSendDataInterval = 60000

  /** When true the beacon should send data to the api */
  beaconSendDataEnabled = true

  /** The number of milliseconds between events */
  eventInterval = 180000

  /** The video player position */
  position = 0

  /** The time the analytics were sent */
  lastAnalyticsUpdateSentTimestamp = 0

  /** The last event update timestamp */
  lastEventUpdateTimestamp = 0

  /** An error code if an error event is triggered */
  errorCode = null

  /** An error message if an error event is triggered */
  errorMsg = null

  /** The number of seek events */
  seekCount = 0

  /**
   * The number of sleeps
   * @deprecated Sleeping events are only the difference between a pause and a play event
   */
  sleepCount = 0

  /**
   * Time code histogram
   * @type {Number[]}
   */
  timecodeHistory = []

  /**
   * The last event update data that was sent compare for changes
   * @type {SMFAnalyticsEvent}
   */
  lastEventStData = {}

  /**
   * do not send engage data to GA again if isEngagedEventSent == true
   * @type {boolean}
   * */
  isEngagedEventSent = false

  /**
   * do not send interested data to GA again if isInterestedEventSent == true
   * @type {boolean}
   * */
  isInterestedEventSent = false

  /**
   * do not send 5 min view data to GA again if isFiveMinViewEventSent == true
   * @type {boolean}
   * */
  isFiveMinViewEventSent = false

  constructor (data = {}) {
    const {
      anonymousUuid,
      duration,
      jwt,
      mediaId,
      mediaLang,
      uid,
      videoId,
      recommId,
      onVideoEngagedEvent,
      onVideoInterestedEvent,
      onVideoFiveMinViewEvent,
      playerType,
    } = data

    this.anonymousUuid = anonymousUuid
    this.duration = duration
    this.jwt = jwt
    this.mediaId = mediaId
    this.mediaLang = mediaLang
    this.uid = uid
    this.videoId = videoId
    this.recommId = recommId
    this.onVideoEngagedEvent = onVideoEngagedEvent
    this.onVideoInterestedEvent = onVideoInterestedEvent
    this.onVideoFiveMinViewEvent = onVideoFiveMinViewEvent
    this.playerType = playerType || VA_DEFAULT_PLAYER_TYPE
  }

  /** Set the player position */
  setPosition (position) {
    if (position > 0) {
      this.position = position
    }
  }

  /** Get the player position */
  getPosition () {
    return this.position
  }

  /** Set the jwt for the user to make api calls */
  setJwt (jwt) {
    this.jwt = jwt
  }

  /** Get the jwt for the user used to make api calls */
  getJwt () {
    return this.jwt
  }

  /** Set the isEngageEventSent value (set "true" once data is sent ot GA) */
  setIsEngagedEventSent (value) {
    this.isEngagedEventSent = value
  }

  /** Get the isEngagedEventSent value */
  getIsEngagedEventSent () {
    return this.isEngagedEventSent
  }

  /** Set the isInterestedEventSent value (set "true" once data is sent ot GA) */
  setIsInterestedEventSent (value) {
    this.isInterestedEventSent = value
  }

  /** Get the isInterestedEventSent value */
  getIsInterestedEventSent () {
    return this.isInterestedEventSent
  }

  /** Set the isFiveMinViewEventSent value (set "true" once data is sent ot GA) */
  setIsFiveMinViewEventSent (value) {
    this.isFiveMinViewEventSent = value
  }

  /** Get the isFiveMinViewEventSent value */
  getIsFiveMinViewEventSent () {
    return this.isFiveMinViewEventSent
  }

  /** Handling updated data from video analytics service
   * @param {Object} video analytics service response
  */
  handleSessionUpdate (data) {
    // Handle the video engaged event
    if (data && data.engaged && !this.getIsEngagedEventSent()) {
      this.setIsEngagedEventSent(true)
      if (_isFunction(this.onVideoEngagedEvent) && this.getStId()) {
        this.onVideoEngagedEvent(this.getStId())
      }
    }

    // Handle the video interested event
    if (data && data.userIsInterested && !this.getIsInterestedEventSent()) {
      this.setIsInterestedEventSent(true)
      if (_isFunction(this.onVideoInterestedEvent) && this.getStId()) {
        this.onVideoInterestedEvent(this.getStId())
      }
    }

    // Handle the video 5 min view event
    if (data && data.userIsAt5Minutes && !this.getIsFiveMinViewEventSent()) {
      this.setIsFiveMinViewEventSent(true)
      if (_isFunction(this.onVideoFiveMinViewEvent) && this.getStId()) {
        this.onVideoFiveMinViewEvent(this.getStId())
      }
    }
  }

  /**
   * Update the session data in video Analytics
   * @param {Object} data The data to send to the endpoint
   */
  async updateSession (data) {
    await this.startSession()
    const stTokenValid = _isString(this.stToken) && _size(this.stToken) === 32
    if (stTokenValid) {
      try {
        const res = await videoAnalyticsStUpdate({
          stId: this.getStId(),
          stToken: this.stToken,
          data,
          auth: this.jwt,
        })
        // Stop sending data if beacon set...server detected bad data.
        if (res.stopBeacon) {
          // Used to set this.stopBeacon
          this.disableBeaconSendData()
        }
        this.handleSessionUpdate(res)
      } catch (e) {
        logError('Unable to stringify data for StUpd for service=VA')
      }
    }
  }

  /**
   * Create the video analytics st data
   * @param {String} type The data type e.g. 'default' or 'extended'
   */
  createStData (type = SMF_ANALYTICS_ST_DATA_TYPE_DEFAULT) {
    const now = getCurrentTimeStamp()
    const lastEvent = _last(this.events)
    return {
      lastPlayState: lastEvent ? lastEvent.name : 'Unknown',
      position: this.position,
      lastEventUpd: now,
      anonymousUuid: this.anonymousUuid,
      irdetoCode: this.errorCode,
      irdetoMsg: this.errorMsg,
      sleepCount: type === SMF_ANALYTICS_ST_DATA_TYPE_EXTENDED ? this.sleepCount : undefined,
      seekCount: type === SMF_ANALYTICS_ST_DATA_TYPE_EXTENDED ? this.seekCount : undefined,
      histogram: type === SMF_ANALYTICS_ST_DATA_TYPE_EXTENDED ? {
        timecodeHistory: _map(this.timecodeHistory, (v) => {
          // Map all null values back to zero
          return _isNil(v) ? 0 : v
        }),
        histogramInterval: this.beaconInterval,
        histogramResolution: this.histogramResolution,
      } : undefined,
      events: type === SMF_ANALYTICS_ST_DATA_TYPE_EXTENDED ? _cloneDeep(this.events) : undefined,
    }
  }

  /** Send the final video analytics data */
  async finalStUpd () {
    return this.updateSession(this.createStData(SMF_ANALYTICS_ST_DATA_TYPE_EXTENDED))
  }

  /** Get the poll interval */
  getBeaconInterval () {
    return this.beaconInterval
  }

  /** Get the st id */
  getStId () {
    return this.stId
  }

  /** Get the VA uuid */
  getDtVAUuid () {
    return this.stVAUuid
  }

  /** Get the st token */
  getStToken () {
    return this.stToken
  }

  /**
   * The beacon timeout callback which calculates video analytics
   * @param {Object} options The options
   * @param {Object} options.player A VideoJS player instance
   */
  async beaconTimeoutCallback (options = {}) {
    const { player } = options
    if (!player) {
      throw new Error('The player options is required')
    }
    const now = getCurrentTimeStamp()
    // Need this for BC player errors or when the player doesn't load.
    const position = getPlayerPosition(player)
    this.setPosition(position)
    // Increment a timecode histogram bin when the player is playing
    if (!player.paused()) {
      const bin = Math.floor((position * 1000) / this.histogramResolution)
      if (_isUndefined(this.timecodeHistory[bin])) {
        this.timecodeHistory[bin] = 1
      } else {
        this.timecodeHistory[bin] += 1
      }
    }
    this.lastBeaconPosition = position
    this.lastBeaconTimestamp = now
    if (this.lastAnalyticsUpdateSentTimestamp === 0) {
      // Handle initial session
      this.lastAnalyticsUpdateSentTimestamp = now
      await this.startSession()
    } else if ((now - this.lastAnalyticsUpdateSentTimestamp) > this.beaconSendDataInterval) {
      // Call stUpd() if beaconInterval has been reached
      this.lastAnalyticsUpdateSentTimestamp = now
      // When we have passed the event interval time send extended data to the service
      const eventIntervalPassed = (now - this.lastEventUpdateTimestamp) > this.eventInterval
      let sendUpdate = true
      const data = this.createStData(
        // eslint-disable-next-line max-len
        eventIntervalPassed ? SMF_ANALYTICS_ST_DATA_TYPE_EXTENDED : SMF_ANALYTICS_ST_DATA_TYPE_DEFAULT,
      )
      // Send events and histrogram data if eventInterval has been reached.
      if (eventIntervalPassed) {
        this.lastEventUpdateTimestamp = now
        if (_isEqual(data, this.lastEventStData)) {
          // Don't send data the events haven't changed
          sendUpdate = false
        } else {
          // Event data change send it
          sendUpdate = true
          // Event data change record this data for later checks
          this.lastEventStData = data
        }
      }
      if (sendUpdate && this.beaconSendDataEnabled) {
        await this.updateSession(data)
      }
    }
  }

  /** Disable sending the beacon data */
  disableBeaconSendData () {
    this.beaconSendDataEnabled = false
  }

  /** Enable sending the beacon data */
  enableBeaconSendData () {
    this.beaconSendDataEnabled = true
  }

  /** Start a session if it hasn't already bee started */
  async startSession () {
    // Session already started or no media id to track
    if (this.stId || _isNil(this.mediaId)) {
      return
    }
    // Send data to collection service
    const data = await videoAnalyticsStAdd({
      id: this.mediaId,
      data: {
        location: document ? _get(document, 'location.href', '') : '',
        anonymousUuid: this.anonymousUuid,
        viewerId: this.uid,
        language: this.mediaLang,
        type: this.playerType,
        extraNid: _isNil(this.videoId) ? undefined : this.videoId,
        recommId: _isNil(this.recommId) ? undefined : this.recommId,
      },
      auth: this.jwt,
    })
    // Check for success.
    if (Number(data.status) !== 1) {
      return
    }
    this.stId = _get(data, 'row.id')
    this.stToken = _get(data, 'token')
    this.stVAUuid = _get(data, 'row.uuid')
  }

  /**
   * Push video player event onto the events array
   * @param {String} name The event name
   * @param {Object} [additionalProps] Optional additional props for the event
   */
  pushEvent (name, player, additionalProps) {
    if (_size(this.events) >= MAX_EVENT_SIZE) {
      return
    }
    if (name === 'Seeking') {
      this.seekCount += 1
    }
    this.events.push(createEvent(name, player, additionalProps))
  }

  /** Handle the video player stopped state */
  stopped (player) {
    this.pushEvent('Stopped', player)
    this.finalStUpd()
    this.disableBeaconSendData()
  }

  /**
   * Push a rate change event
   * @param {String} rate The playback rate
   */
  pushRateChangeEvent (rate, player) {
    switch (rate) {
      case 0.5:
        this.pushEvent('ChangeSpeed0.5x', player)
        break
      case 0.75:
        this.pushEvent('ChangeSpeed0.75x', player)
        break
      case 1.0:
        this.pushEvent('ChangeSpeed1.0x', player)
        break
      case 1.5:
        this.pushEvent('ChangeSpeed1.5x', player)
        break
      case 1.75:
        this.pushEvent('ChangeSpeed1.75x', player)
        break
      case 2.0:
        this.pushEvent('ChangeSpeed2.0x', player)
        break
      default:
        break
    }
  }

  /**
   * Handle a media faile player state
   * @param {Number} errorCode The error code
   * @param {String} errorMessage The error message
   */
  mediaFailed (errorCode, errorMessage, player) {
    // check for first time we have this event (only report ONCE!!!)
    if (_findIndex(this.events, { name: 'MediaFailed' }) !== -1) {
      // if we have already reported error, don't keep sending to server!!!
      return
    }
    this.pushEvent('MediaFailed', player)

    // Set the Irdeto error code and type (reusing existing fields).
    this.errorCode = errorCode
    this.errorMsg = errorMessage

    // Send the final update.
    this.finalStUpd()
    this.disableBeaconSendData()
  }
}

/**
 * A video-js plugin which collects video player analytics
 * @param {*} options The options
 * @param {Number} [options.uid] The user id
 * @param {String} [options.jwt] The authentication JWT
 * @param {Number} options.mediaId The media id
 * @param {Number} options.videoId The video content id
 * @param {String} options.mediaLang The media language
 * @param {Number} options.duration The media duration
 * @param {String} [options.anonymousUuid] The anonymous uuid id the user is not logged in
 */
function SMFAnalyticsPlugin (options = {}) {
  const player = this
  /** The last text track language which was shown */
  let textTrackLang = ''
  const { duration = 0 } = options
  if (duration <= 0) {
    throw new Error('Cannot initialize video analytics with a duration of zero')
  }
  const {
    onPlayEvent,
    onQualifiedViewEvent,
    onVideoEngagedEvent,
    onVideoInterestedEvent,
    onWatchCompletedEvent,
    onVideoAudioChangedEvent,
    onVideoSubtitlesSetEvent,
    onVideoFiveMinViewEvent,
    pictureInPictureToggled,
    onChromecastConnected,
    onAirplayConnected,
    onSpeedChanged,
  } = options
  player.ready(function smfAnalyticsOnReady () {
    const smfAnalytics = new SMFAnalytics({
      uid: options.uid,
      jwt: options.jwt,
      mediaId: options.mediaId,
      videoId: options.videoId,
      mediaLang: options.mediaLang,
      duration: options.duration,
      anonymousUuid: options.anonymousUuid,
      recommId: options.recommId,
      playerType: options.playerType,
      onVideoEngagedEvent,
      onVideoInterestedEvent,
      onVideoFiveMinViewEvent,
    })
    smfAnalytics.pushEvent('EmbedPlayer', this)
    smfAnalytics.pushEvent('PlayerReady', this)

    /**
     * Window unload listener which attempts to capture last video analytics before losing window
     * @param {Object} event A window event
     */
    function onWindowUnloadListener (event) {
      if (event && event.persisted) {
        // the page isn't being discarded, so it can be reused later
        return
      }
      // Trigger player dispose to fire last events collection
      player.dispose()
      clearWindowUnloadListener()
    }

    /** Clear window unload listener for polling video player analytics */
    function clearWindowUnloadListener () {
      window.removeEventListener('unload', onWindowUnloadListener)
    }

    if (window) {
      // Add listener to catch event on window unload
      window.addEventListener('unload', onWindowUnloadListener)
    }

    /* do not send qualidied data to GA again if isQualifiedViewEventSent == true */
    let isQualifiedViewEventSent = false

    /* qualified view event timeout  */
    let qualifiedViewTimeout

    /* Clear timeout for qualified view event */
    function clearQualifiedViewTimeout () {
      if (qualifiedViewTimeout) {
        this.clearTimeout(qualifiedViewTimeout)
      }
    }

    /** The Beacon timeout id */
    let beaconTimeoutId

    /** Clear timeout handler for polling video player analytics */
    function clearTimeoutBeacon () {
      // Clear the current timeout before setting another
      if (beaconTimeoutId) {
        this.clearTimeout(beaconTimeoutId)
      }
    }

    /** Timeout handler for beacon collecting video player analytics */
    function onTimeoutBeacon () {
      // Clear the current timeout before setting another
      clearTimeoutBeacon.bind(this)()
      try {
        smfAnalytics.beaconTimeoutCallback({ player: this })
      } catch (_) {
        // Do nothing allow polling to continue
      }
      const position = getPlayerPosition(this)
      // The next poll interval is specified to land within
      // histrogram bin boundries (a sort of PLL).
      const beaconInterval = smfAnalytics.getBeaconInterval()
      const nextBeaconInterval = Math.round(
        (beaconInterval * 1.5) - ((position * 1000) % beaconInterval),
      )
      beaconTimeoutId = this.setTimeout(onTimeoutBeacon, nextBeaconInterval)
    }

    // Start polling
    beaconTimeoutId = this.setTimeout(onTimeoutBeacon, smfAnalytics.getBeaconInterval() / 2)
    // Set the initial state for the JWT
    this.setState({ jwt: options.jwt })
    // Listen for state changes to the jwt
    this.on('statechanged', function onStateChanged (e) {
      if (e.changes && e.changes.jwt) {
        smfAnalytics.setJwt(e.changes.jwt)
      }
    })
    // The player is "this"
    this.on('loadstart', function loadedmetadata () {
      smfAnalytics.pushEvent('Opening', this)
    })
    this.on('loadedmetadata', function loadedmetadata () {
      smfAnalytics.setPosition(getPlayerPosition(this))
    })
    this.on('texttrackchange', function texttrackchange () {
      _forEach(this.textTracks(), (t) => {
        if (t.mode === 'showing' && textTrackLang !== t.language) {
          textTrackLang = t.language
          smfAnalytics.pushEvent('ShowTextTrack', this, {
            kind: t.kind,
            lang: t.language,
          })
        }
      })
    })
    this.on('seeking', function seeking () {
      const position = getPlayerPosition(this)
      smfAnalytics.setPosition(position)
      smfAnalytics.pushEvent('Seeking', this)
    })
    this.one('play', function play () {
      const position = getPlayerPosition(this)
      smfAnalytics.setPosition(position)
    })
    this.on('playing', function playing () {
      const position = getPlayerPosition(this)
      smfAnalytics.setPosition(position)
      smfAnalytics.pushEvent('Playing', this)

      /* Starting qualifiedViewTimeout for tracking event */
      if (_isFunction(onQualifiedViewEvent)
        && !isQualifiedViewEventSent) {
        qualifiedViewTimeout = this.setTimeout(() => {
          onQualifiedViewEvent(smfAnalytics.getStId())
          isQualifiedViewEventSent = true
        }, QUALIFIED_VIEW_DURATION)
      }
    })
    this.on('pause', function pause () {
      const position = getPlayerPosition(this)
      smfAnalytics.setPosition(position)
      smfAnalytics.pushEvent('Paused', this)
      clearQualifiedViewTimeout.bind(this)()
    })
    // Keep the analytics position up to date
    this.on('timeupdate', function timeupdateAnalyticsPosition () {
      const position = getPlayerPosition(this)
      smfAnalytics.setPosition(position)
    })
    this.on('error', function error () {
      const mediaError = this.error()
      if (!mediaError || typeof mediaError !== 'object') {
        return
      }
      let errorMessage
      if (_isString(mediaError.status) || _isNumber(mediaError.status)) {
        errorMessage = `${mediaError.message} (Status ${mediaError.status})`
      } else {
        errorMessage = mediaError.message
      }
      const codeDesc = MEDIA_ERROR_CODE_DESCRIPTIONS[mediaError.code] ?? `Unknown player error (code: ${mediaError.code})`
      const descriptiveMessage = errorMessage ? `${codeDesc}: ${errorMessage}` : codeDesc
      smfAnalytics.mediaFailed(mediaError.code, descriptiveMessage, this)
      // Stop collecting and sending metrics
      clearTimeoutBeacon.bind(this)()
      clearQualifiedViewTimeout.bind(this)()
    })
    this.on('ratechange', function ratechange () {
      const playbackRate = this.playbackRate()
      smfAnalytics.pushRateChangeEvent(playbackRate, this)
    })
    this.on('enterpictureinpicture', function enterpictureinpicture () {
      smfAnalytics.pushEvent('ViewMode', this, { kind: 'PIPOn' })
    })
    this.on('leavepictureinpicture', function leavepictureinpicture () {
      smfAnalytics.pushEvent('ViewMode', this, { kind: 'PIPOff' })
    })
    this.one(['ended', 'dispose'], function handleEndOrDispose () {
      smfAnalytics.setPosition(getPlayerPosition(this))
      smfAnalytics.stopped(this)
      clearTimeoutBeacon.bind(this)()
      clearQualifiedViewTimeout.bind(this)()
      clearWindowUnloadListener()
    })
    if (_isFunction(pictureInPictureToggled)) {
      this.on('enterpictureinpicture', function enterpictureinpicture () {
        if (smfAnalytics.getStId()) {
          pictureInPictureToggled(smfAnalytics.getStId(), true)
        }
      })
      this.on('leavepictureinpicture', function leavepictureinpicture () {
        if (smfAnalytics.getStId()) {
          pictureInPictureToggled(smfAnalytics.getStId(), false)
        }
      })
    }
    if (_isFunction(onWatchCompletedEvent)) {
      this.on('ended', function endedWatchCompletedEvent () {
        if (smfAnalytics.getStId()) {
          // Check that we have the tracking id
          this.off('ended', endedWatchCompletedEvent)
          onWatchCompletedEvent(smfAnalytics.getStId())
        }
      })
    }
    if (_isFunction(onPlayEvent)) {
      this.on('timeupdate', function timeupdateOnPlayEvent () {
        // Check that we have the tracking id and are in the play state
        // this will run until both are true
        // We need to wait for the response from video analytics
        if (smfAnalytics.getStId() && !player.paused()) {
          // Remove the event so it only fires once
          this.off('timeupdate', timeupdateOnPlayEvent)
          onPlayEvent(smfAnalytics.getStId())
        }
      })
    }
    if (_isFunction(onVideoAudioChangedEvent)) {
      player.VideoMediaCenter().on('audio-track-changed', (_, details = {}) => {
        const { lang } = details
        if (lang) {
          onVideoAudioChangedEvent(smfAnalytics.getStId(), lang)
        }
      })
    }
    if (_isFunction(onVideoSubtitlesSetEvent)) {
      player.VideoMediaCenter().on('text-state-changed', (_, details = {}) => {
        const { activeLanguage } = details
        if (activeLanguage) {
          onVideoSubtitlesSetEvent(smfAnalytics.getStId(), activeLanguage)
        }
      })
    }
    if (_isFunction(onChromecastConnected)) {
      player.on('canplay', () => {
        if (player.techName_ === 'ChromecastTech') {
          onChromecastConnected(smfAnalytics.getStId())
        }
      })
    }
    if (_isFunction(onAirplayConnected) && player.bcAirplay()) {
      const airplayMediaEl = player.bcAirplay().mediaEl
      if (airplayMediaEl) {
        airplayMediaEl.addEventListener('webkitcurrentplaybacktargetiswirelesschanged', () => {
          if (airplayMediaEl.webkitCurrentPlaybackTargetIsWireless) {
            onAirplayConnected(smfAnalytics.getStId())
          }
        })
      }
    }
    if (_isFunction(onSpeedChanged)) {
      player.on('ratechange', () => {
        const playbackRate = player.playbackRate()
        onSpeedChanged(smfAnalytics.getStId(), playbackRate)
      })
    }
  })
}

/** Register the video-js plugin */
export function register (videojs = window.videojs) {
  videojs.registerPlugin(PLUGIN_NAME, SMFAnalyticsPlugin)
}
